import dayjs, { Dayjs } from 'dayjs';
import useSymptomTrackingData, { CycleStats } from 'hooks/useSymptomTrackingData';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import LinearCalendarChart from './LinearCalendarChart';
import PeriodChart from './PeriodChart';
import SymptomsChart from './SymptomsChart';
import SymptomsChartLegend from './SymptomsChartLegend';
import { colors } from 'design';

const useStyles = createUseStyles({
  graphSelector: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '12px 0px',
    '& .ant-radio-button-wrapper': {
      border: 'none',
    },
  },
  graphsContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    paddingTop: 18,
    position: 'relative',
  },
  graphsContainerNoScroll: {
    composes: '$graphsContainer',
    overflowX: 'hidden',
  },
  // Progress UI styles
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  progressLabel: {
    fontSize: 14,
    color: colors.text.tertiary,
  },
  progressBarOuter: {
    width: '100%',
    height: 10,
    borderRadius: 999,
    background: colors.surface.page,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    borderRadius: 999,
    background: colors.brand.accent,
    transition: 'width 200ms ease',
  },
  progressText: {
    fontSize: 12,
    color: colors.text.disabled,
  },
  selectedGraph: {
    color: colors.brand.accentStrong,
  },
  unselectedGraphLink: {
    color: colors.text.tertiary,
  },
});

const CompareCycles: React.FC = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  const {
    cycleDaysWithCenteredPeriod,
    getStatusValueForDisplayedDays,
    hasDataOtherThanPeriod,
    dataOtherThanPeriod,
    cycleHistory,
  } = useSymptomTrackingData();

  const notEnoughData = !hasDataOtherThanPeriod || cycleHistory?.length < 2;

  const graphsContainerRef = useRef<HTMLDivElement>(null);

  /*
  const maxLeftOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.min(max, days[0].diff(cycle.start, 'days'));
  }, 0);
  const maxRightOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.max(max, days[days.length - 1].diff(cycle.start, 'days'));
  }, 0);
  */
  const maxLeftOffset = -24;
  const maxRightOffset = 25;

  // scroll to the middle of the container
  useEffect(() => {
    if (graphsContainerRef.current) {
      if (!notEnoughData) {
        const { scrollWidth, clientWidth } = graphsContainerRef.current;
        graphsContainerRef.current.scrollTo({
          left: scrollWidth / 2 - clientWidth / 2,
        });
      } else {
        graphsContainerRef.current.scrollTo({
          left: 0,
        });
      }
    }
  }, [graphsContainerRef, notEnoughData]);

  const [selectedGraph, setSelectedGraph] = useState<'generalState' | 'period' | 'pain' | 'symptoms'>('period');
  const graphs: ('generalState' | 'period' | 'pain' | 'symptoms')[] = ['generalState', 'period', 'pain', 'symptoms'];

  // Progress calculations
  const requiredCycles = 2;
  const cyclesProgress = Math.min(
    100,
    Math.round((Math.min(cycleHistory?.length || 0, requiredCycles) / requiredCycles) * 100),
  );
  const hasAnySymptomDay = (dataOtherThanPeriod.daysWithData?.length || 0) > 0;
  const symptomsProgress = hasAnySymptomDay ? 100 : 0;

  const getGraphLabel = (graph: 'generalState' | 'period' | 'pain' | 'symptoms'): string => {
    switch (graph) {
      case 'generalState':
        return t('symptomTracking.compare.generalState');
      case 'period':
        return t('symptomTracking.compare.period');
      case 'pain':
        return t('symptomTracking.compare.pain');
      case 'symptoms':
        return t('symptomTracking.compare.symptoms');
      default:
        return '';
    }
  };

  const renderGraph = (days: Dayjs[], cycle: CycleStats) => {
    const dayRange: [Dayjs, Dayjs] = [cycle.start.add(maxLeftOffset, 'days'), cycle.start.add(maxRightOffset, 'days')];

    const displayedDays = cycle.predicted ? days.filter(day => day.isSameOrBefore(dayjs(), 'day')) : days;

    const topLabels: [Dayjs, string][] = [
      [
        cycle.start,
        t('symptomTracking.compare.cycleLabel', {
          date: cycle.start.toDate().toLocaleDateString(locale, { month: 'long', day: 'numeric' }),
        }),
      ],
    ];

    const key = days[0].date();
    switch (selectedGraph) {
      case 'generalState':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
            range={[0, 4]}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'period':
        return (
          <PeriodChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
            dayRange={dayRange}
            periodDots={
              cycle.predicted ? Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days')) : []
            } // empty list to maintain a consistent height, but do not display any dots for readability
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'pain':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('pain_level', days)}
            range={[0, 4]}
            reverseColors
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'symptoms':
        return (
          <SymptomsChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {!notEnoughData && (
        <div className={styles.graphSelector}>
          {graphs.map(graph => (
            <a
              key={graph}
              onClick={() => {
                setSelectedGraph(graph);
                posthog.capture('tracking.compare_graph_selected', { graph });
              }}
              className={selectedGraph === graph ? styles.selectedGraph : styles.unselectedGraphLink}
            >
              {getGraphLabel(graph)}
            </a>
          ))}
        </div>
      )}
      <div className={notEnoughData ? styles.graphsContainerNoScroll : styles.graphsContainer} ref={graphsContainerRef}>
        {notEnoughData ? (
          <div className={styles.progressContainer}>
            <div className={styles.progressItem}>
              <div className={styles.progressLabel}>{t('symptomTracking.compare.fillAtLeastTwoCycles')}</div>
              <div className={styles.progressBarOuter}>
                <div className={styles.progressBarInner} style={{ width: `${cyclesProgress}%` }} />
              </div>
              <div className={styles.progressText}>
                {cycleHistory?.length}/{requiredCycles}
              </div>
            </div>
            <div className={styles.progressItem}>
              <div className={styles.progressLabel}>{t('symptomTracking.compare.fillAtLeastOneSymptomDay')}</div>
              <div className={styles.progressBarOuter}>
                <div className={styles.progressBarInner} style={{ width: `${symptomsProgress}%` }} />
              </div>
              <div className={styles.progressText}>{hasAnySymptomDay ? 1 : 0}/1</div>
            </div>
          </div>
        ) : (
          cycleDaysWithCenteredPeriod
            .reverse()
            .slice(0, 4)
            .filter(({ cycle }) => Boolean((cycle as any).start))
            .map(({ days, cycle }) => {
              return renderGraph(days, cycle as CycleStats);
            })
        )}
      </div>
      {selectedGraph === 'symptoms' && !notEnoughData && (
        <SymptomsChartLegend
          dayValues={getStatusValueForDisplayedDays(
            'symptoms',
            cycleDaysWithCenteredPeriod.flatMap(({ days }) => days),
            [],
          )}
        />
      )}
    </div>
  );
};

export default CompareCycles;
